import { useRef, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { useFlashToast } from '../../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilePdf, faCloudArrowUp, faTrash, faDownload,
  faXmark, faSpinner, faUser
} from '@fortawesome/free-solid-svg-icons'
import SuperadminSidebar from '../../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../../components/superadmin/Topbar'

function UploadModal({ onClose }) {
  const fileRef = useRef()
  const { data, setData, post, processing, errors, reset } = useForm({
    title: '',
    description: '',
    file: null,
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    post('/superadmin/documents', {
      forceFormData: true,
      onSuccess: () => { reset(); onClose() },
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-bold text-gray-100">Upload Dokumen PDF</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300">
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Judul Dokumen <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={data.title}
              onChange={e => setData('title', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Masukkan judul dokumen"
            />
            {errors.title && <p className="text-xs text-red-400 mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">Deskripsi</label>
            <textarea
              value={data.description}
              onChange={e => setData('description', e.target.value)}
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Deskripsi singkat (opsional)"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">File PDF <span className="text-red-400">*</span></label>
            <div
              onClick={() => fileRef.current.click()}
              className="border-2 border-dashed border-gray-600 rounded-lg p-5 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-900/20 transition-colors"
            >
              <FontAwesomeIcon icon={faCloudArrowUp} className="w-7 h-7 text-gray-600 mb-2" />
              <p className="text-xs text-gray-500">
                {data.file ? data.file.name : 'Klik untuk pilih file PDF (maks. 50MB)'}
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={e => setData('file', e.target.files[0])}
              />
            </div>
            {errors.file && <p className="text-xs text-red-400 mt-1">{errors.file}</p>}
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 text-sm text-gray-400 border border-gray-600 rounded-lg hover:bg-gray-700">
              Batal
            </button>
            <button type="submit" disabled={processing}
              className="flex-1 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-60 flex items-center justify-center gap-2">
              {processing && <FontAwesomeIcon icon={faSpinner} className="w-3.5 h-3.5 animate-spin" />}
              Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function SuperadminDocumentsIndex({ current_user, documents }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showUpload, setShowUpload] = useState(false)

  const handleDelete = (doc) => {
    if (confirm(`Hapus dokumen "${doc.title}"?`)) {
      import('@inertiajs/react').then(({ router }) => {
        router.delete(`/superadmin/documents/${doc.id}`)
      })
    }
  }

  return (
    <div className="flex h-screen bg-gray-950 font-sans overflow-hidden text-gray-100">
      <SuperadminSidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <SuperadminTopbar current_user={current_user} onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-100">Dokumen PDF</h1>
              <p className="text-sm text-gray-500 mt-0.5">Kelola semua dokumen PDF yang diunggah</p>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              <FontAwesomeIcon icon={faCloudArrowUp} className="w-4 h-4" />
              Upload Dokumen
            </button>
          </div>

          {/* Documents list */}
          {documents.length === 0 ? (
            <div className="text-center py-20 text-gray-600">
              <FontAwesomeIcon icon={faFilePdf} className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">Belum ada dokumen yang diunggah</p>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-800/50 border-b border-gray-800">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dokumen</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Diunggah Oleh</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Ukuran</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Tanggal</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {documents.map(doc => (
                    <tr key={doc.id} className="hover:bg-gray-800/40 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-red-900/40 flex items-center justify-center flex-shrink-0">
                            <FontAwesomeIcon icon={faFilePdf} className="w-4 h-4 text-red-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-200">{doc.title}</div>
                            {doc.description && <div className="text-xs text-gray-500 truncate max-w-xs">{doc.description}</div>}
                            <div className="text-xs text-gray-600">{doc.filename}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <FontAwesomeIcon icon={faUser} className="w-3 h-3" />
                          <span>{doc.uploaded_by}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{formatSize(doc.size)}</td>
                      <td className="px-5 py-3.5 text-gray-500 hidden lg:table-cell">{doc.created_at}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 justify-end">
                          <a href={doc.url}
                            className="p-1.5 text-gray-500 hover:text-indigo-400 rounded-lg hover:bg-indigo-900/30 transition-colors"
                            title="Download">
                            <FontAwesomeIcon icon={faDownload} className="w-3.5 h-3.5" />
                          </a>
                          <button
                            onClick={() => handleDelete(doc)}
                            className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-red-900/30 transition-colors"
                            title="Hapus">
                            <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} />}
    </div>
  )
}
